import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, PhoneCall, ShieldCheck, Zap, Server } from "lucide-react";
import NetworkAnimation from "@/components/NetworkAnimation";
import { LOGO_URL } from "@/lib/constants";

export default function Hero() {
    return (
        <section className="relative overflow-hidden bg-white" data-testid="hero-section">
            <div className="absolute inset-0 grid-bg opacity-70" />
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-brand-soft rounded-full blur-3xl opacity-70 pointer-events-none" />
            <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-brand/10 rounded-full blur-3xl opacity-70 pointer-events-none" />

            <div className="container-lg relative pt-16 pb-20 lg:pt-24 lg:pb-28">
                <div className="grid lg:grid-cols-12 gap-10 items-center">
                    <motion.div
                        className="lg:col-span-7"
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                    >
                        <img
                            src={LOGO_URL}
                            alt="CoreCyrus"
                            className="h-24 sm:h-28 lg:h-32 w-auto mb-6"
                            data-testid="hero-logo"
                        />
                        <div className="inline-flex items-center gap-2 rounded-full border border-brand/30 bg-brand-soft/60 px-4 py-1.5 text-xs font-bold tracking-[0.2em] uppercase text-brand mb-6">
                            <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse-dot" />
                            Enterprise Networking · Since 2014
                        </div>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-navy leading-[1.05]"
                            data-testid="hero-headline"
                        >
                            Enterprise Networking Solutions{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10 text-brand">from Design to Deployment</span>
                                <span className="absolute inset-x-0 bottom-1 h-3 bg-brand/10 -z-0" />
                            </span>
                        </h1>
                        <p
                            className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed"
                            data-testid="hero-subheadline"
                        >
                            Complete Networking Infrastructure · Enterprise Products · Installation · AMC ·
                            Technical Support — engineered for businesses, campuses and data centers across India.
                        </p>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/inquiry?type=quote"
                                data-testid="hero-cta-quote"
                                className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3.5 text-sm font-semibold hover:bg-brand-hover transition-colors shadow-lg shadow-brand/20"
                            >
                                Request a Quote <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/inquiry?type=consultation"
                                data-testid="hero-cta-expert"
                                className="inline-flex items-center gap-2 rounded-full bg-white text-navy border border-slate-200 px-6 py-3.5 text-sm font-semibold hover:border-navy hover:bg-slate-50 transition-colors"
                            >
                                <PhoneCall className="w-4 h-4" /> Talk to an Expert
                            </Link>
                        </div>

                        <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
                            {[
                                { k: "500+", v: "Deployments" },
                                { k: "24×7", v: "Support" },
                                { k: "50+", v: "Enterprise Brands" },
                            ].map((s) => (
                                <div key={s.v}>
                                    <div className="font-display text-3xl font-extrabold text-navy" data-testid={`hero-stat-${s.v.toLowerCase().replace(/\s+/g, "-")}`}>{s.k}</div>
                                    <div className="text-xs font-medium tracking-wider uppercase text-slate-500 mt-1">{s.v}</div>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        className="lg:col-span-5 relative"
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut", delay: 0.15 }}
                    >
                        <div className="relative aspect-square rounded-3xl overflow-hidden border border-slate-200 bg-gradient-to-br from-white via-brand-soft/40 to-white shadow-[0_20px_60px_rgba(10,37,64,0.08)]">
                            <img
                                src="https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?w=1200&q=80"
                                alt="Enterprise data center"
                                className="absolute inset-0 w-full h-full object-cover opacity-30"
                            />
                            <NetworkAnimation className="opacity-90" />
                            <div className="absolute inset-x-6 bottom-6 grid grid-cols-3 gap-2">
                                {[
                                    { i: Server, t: "Core" },
                                    { i: ShieldCheck, t: "Secure" },
                                    { i: Zap, t: "Fast" },
                                ].map((b) => (
                                    <div key={b.t} className="rounded-2xl bg-white/80 backdrop-blur-md border border-white p-3 shadow-sm">
                                        <b.i className="w-5 h-5 text-brand" />
                                        <div className="mt-2 text-xs font-semibold text-navy">{b.t}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
